import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ShieldPlus, Trash2, Key, X, Copy, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';

/**
 * Dedicated section to manage other Administrators.
 * - Lists all existing admins (excluding self)
 * - Creates new admins with a prominent button (no hidden checkbox)
 * - Reset password + delete per admin
 */
export default function AdminManagers() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPwFor, setShowPwFor] = useState(null); // admin object
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ username: '', password: '' });
  const [newPw, setNewPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null); // {username, password}
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/users');
      setAdmins(data.filter((u) => u.is_admin));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const suggestPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let out = '';
    for (let i = 0; i < 10; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
    setForm((f) => ({ ...f, password: out }));
  };

  const create = async () => {
    const username = form.username.trim().toLowerCase();
    if (!username || form.password.length < 6) {
      return toast.error('Username e password (min 6 caratteri) sono obbligatori');
    }
    setBusy(true);
    try {
      await api.post('/users', { username, password: form.password, is_admin: true });
      toast.success(`Admin "${username}" creato`);
      setCreatedInfo({ username, password: form.password });
      setForm({ username: '', password: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione admin');
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`Admin "${deleteTarget.username}" eliminato`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setBusy(false);
    }
  };

  const resetPw = async () => {
    if (!showPwFor || newPw.length < 6) return toast.error('Password min 6 caratteri');
    setBusy(true);
    try {
      await api.put(`/users/${showPwFor.id}/password`, { password: newPw });
      toast.success(`Password aggiornata per "${showPwFor.username}"`);
      setShowPwFor(null);
      setNewPw('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setBusy(false);
    }
  };

  const copyCreds = async () => {
    if (!createdInfo) return;
    const text = `Username: ${createdInfo.username}\nPassword: ${createdInfo.password}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  const closeCreateFlow = () => {
    setShowCreate(false);
    setCreatedInfo(null);
    setCopied(false);
  };

  return (
    <div className="space-y-3" data-testid="admin-managers-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/50">Amministratori</div>
          <div className="text-[11px] text-white/40 mt-0.5">
            Gestisci gli altri admin del sistema. Tu (<span className="text-[var(--gold)]">{user?.username}</span>) sei escluso dalla lista.
          </div>
        </div>
        <button
          data-testid="create-admin-btn"
          onClick={() => setShowCreate(true)}
          className="btn-gold px-4 py-2 flex items-center gap-2 text-sm whitespace-nowrap"
        >
          <ShieldPlus className="w-4 h-4" />
          Nuovo Admin
        </button>
      </div>

      <div className="space-y-2">
        {loading && <div className="text-center text-xs text-white/40 py-4">Caricamento…</div>}
        {!loading && admins.length === 0 && (
          <div className="card-surface p-5 text-center" data-testid="no-admins-hint">
            <Shield className="w-6 h-6 text-[var(--gold)]/70 mx-auto mb-2" />
            <div className="text-sm text-white/70">Sei l'unico amministratore.</div>
            <div className="text-[11px] text-white/40 mt-1">Crea un secondo admin per avere un backup di accesso.</div>
          </div>
        )}
        {admins.map((a) => (
          <div
            key={a.id}
            data-testid={`admin-row-${a.username}`}
            className="card-surface p-3 flex items-center gap-3"
          >
            <div className="relative">
              <Avatar username={a.username} avatarUrl={a.avatar_url} size={40} />
              <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[var(--gold)] border-2 border-[var(--bg-void)] flex items-center justify-center">
                <Shield className="w-2.5 h-2.5 text-black" strokeWidth={3} />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-medium text-white truncate flex items-center gap-2">
                {a.username}
                {a.must_change_password && (
                  <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full tracking-wide">
                    da cambiare password
                  </span>
                )}
              </div>
              <div className="text-[10px] text-white/40 tracking-wider uppercase">Amministratore</div>
            </div>
            <button
              data-testid={`admin-pw-${a.username}`}
              onClick={() => { setShowPwFor(a); setNewPw(''); }}
              className="p-2 text-white/60 hover:text-[var(--gold)]"
              title="Reset password"
            >
              <Key className="w-4 h-4" />
            </button>
            <button
              data-testid={`admin-delete-${a.username}`}
              onClick={() => setDeleteTarget(a)}
              className="p-2 text-white/60 hover:text-red-400"
              title="Elimina"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Create modal */}
      <AnimatePresence>
        {showCreate && (
          <ModalShell title={createdInfo ? 'Admin creato' : 'Nuovo Amministratore'} onClose={closeCreateFlow}>
            {!createdInfo ? (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Username</label>
                  <input
                    data-testid="new-admin-username"
                    className="input-gold"
                    placeholder="es. mario.rossi"
                    value={form.username}
                    autoFocus
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Password iniziale</label>
                  <div className="flex gap-2">
                    <input
                      data-testid="new-admin-password"
                      type="text"
                      className="input-gold flex-1 font-mono"
                      placeholder="min 6 caratteri"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <button
                      type="button"
                      data-testid="new-admin-suggest"
                      onClick={suggestPassword}
                      className="px-3 rounded-xl border border-[var(--border-subtle)] text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 text-xs"
                    >
                      Genera
                    </button>
                  </div>
                  <p className="text-[10px] text-white/40 mt-2 leading-relaxed">
                    Il nuovo admin dovrà cambiare questa password al primo accesso. Riceve tutti i permessi (chat, utenti, contenuti, broadcast, impostazioni).
                  </p>
                </div>
                <button
                  data-testid="new-admin-submit"
                  onClick={create}
                  disabled={busy}
                  className="btn-gold w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <ShieldPlus className="w-4 h-4" /> Crea Amministratore
                </button>
              </>
            ) : (
              <div className="space-y-3" data-testid="admin-created-success">
                <div className="rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/8 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">Salva queste credenziali</div>
                  <div className="text-sm text-white/90">
                    <div><span className="text-white/50">Username: </span><span className="font-mono text-[var(--gold)]">{createdInfo.username}</span></div>
                    <div className="mt-1"><span className="text-white/50">Password: </span><span className="font-mono text-[var(--gold)]">{createdInfo.password}</span></div>
                  </div>
                </div>
                <button
                  data-testid="copy-admin-creds"
                  onClick={copyCreds}
                  className="w-full py-3 rounded-full border border-[var(--border-subtle)] text-white/85 hover:border-[var(--gold)]/50 hover:text-white transition-colors text-sm flex items-center justify-center gap-2"
                >
                  {copied ? <><Check className="w-4 h-4 text-[var(--gold)]" /> Copiato</> : <><Copy className="w-4 h-4" /> Copia credenziali</>}
                </button>
                <button
                  data-testid="admin-created-done"
                  onClick={closeCreateFlow}
                  className="btn-gold w-full py-3"
                >
                  Chiudi
                </button>
                <p className="text-[10px] text-white/40 text-center">Al primo login verrà richiesto il cambio password.</p>
              </div>
            )}
          </ModalShell>
        )}

        {showPwFor && (
          <ModalShell title={`Reset password — ${showPwFor.username}`} onClose={() => setShowPwFor(null)}>
            <input
              data-testid="reset-admin-pw-input"
              type="text"
              className="input-gold font-mono"
              placeholder="Nuova password (min 6)"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoFocus
            />
            <p className="text-[10px] text-white/40">L'admin dovrà cambiare questa password al prossimo login.</p>
            <button
              data-testid="reset-admin-pw-submit"
              onClick={resetPw}
              disabled={busy}
              className="btn-gold w-full py-3 disabled:opacity-60"
            >
              Aggiorna password
            </button>
          </ModalShell>
        )}

        {deleteTarget && (
          <ModalShell title="Conferma eliminazione" onClose={() => setDeleteTarget(null)}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-sm text-white/80 leading-relaxed pt-1">
                Eliminare l'amministratore <span className="font-semibold text-[var(--gold)]">"{deleteTarget.username}"</span>?
                <br />
                <span className="text-xs text-white/50">Perderà immediatamente accesso al pannello.</span>
              </p>
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-full border border-[var(--border-subtle)] text-white/80 hover:border-[var(--gold)]/50 hover:text-white transition-colors text-sm font-medium"
              >
                Annulla
              </button>
              <button
                data-testid="admin-delete-confirm"
                onClick={del}
                disabled={busy}
                className="flex-1 py-3 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors text-sm disabled:opacity-60"
              >
                Elimina
              </button>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>
    </div>
  );
}

function ModalShell({ children, title, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 40, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        className="card-surface p-5 w-full max-w-sm space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium">{title}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}
